import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import Base from '../components/Base';
import AuthContext from '../context/AuthContext';
import {
  BarChart3, TrendingUp, ShoppingBag, IndianRupee, Package,
  Eye, Users, Search, ChevronLeft, ChevronRight, Globe, Monitor,
  UserPlus, UserCheck, ArrowDown, Clock, Lightbulb, Download,
  ShoppingCart, AlertTriangle, CheckCircle, Info, XCircle, X,
  Smartphone, Tablet, ArrowUpRight, ArrowDownRight, Star, Repeat
} from 'lucide-react';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
// ── Constants ───────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'traffic', label: 'Traffic', icon: Globe },
  { id: 'products', label: 'Products', icon: Eye },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'insights', label: 'Insights', icon: Lightbulb },
];

const DAY_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const PAGE_TYPE_LABELS = {
  home: 'Home', shop: 'Shop', product: 'Product', category: 'Category',
  search: 'Search', cart: 'Cart', checkout: 'Checkout', other: 'Other',
};

const PAGE_TYPE_COLORS = {
  home: '#d45614', shop: '#3B82F6', product: '#10B981', category: '#8B5CF6',
  search: '#F59E0B', cart: '#EC4899', checkout: '#6366F1', other: '#9CA3AF',
};

const FUNNEL_COLORS = ['#d45614', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'];
const FUNNEL_LABELS = { home: 'Home', shop: 'Shop', product: 'Product', cart: 'Cart', checkout: 'Checkout' };

const DEVICE_COLORS = { Mobile: '#d45614', Desktop: '#3B82F6', Tablet: '#10B981' };

const ALERT_STYLES = {
  success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: CheckCircle, iconColor: 'text-emerald-500' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: AlertTriangle, iconColor: 'text-amber-500' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: Info, iconColor: 'text-blue-500' },
  danger: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: XCircle, iconColor: 'text-red-500' },
};

const fmt = (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`;

// ── CSV Export Utility ─────────────────────────────────────────────────────
const downloadCSV = (headers, rows, filename) => {
  const escape = (val) => {
    const str = String(val ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

// ── Reusable Components ─────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, bg, sub, growth }) => (
  <div className="bg-white rounded-[9px] border border-gray-200 p-4 sm:p-5 shadow-sm">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs sm:text-sm text-gray-500 font-medium">{label}</p>
      <div className="w-8 h-8 rounded-[9px] flex items-center justify-center" style={{ backgroundColor: bg, color }}>
        {icon}
      </div>
    </div>
    <p className="text-2xl sm:text-3xl font-bold text-[#2C1810] font-fell">{value}</p>
    {growth !== undefined && growth !== null && (
      <div className={`flex items-center gap-1 mt-1 ${growth >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
        {growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        <span className="text-xs font-medium">{growth >= 0 ? '+' : ''}{growth}%</span>
        <span className="text-xs text-gray-400 ml-0.5">vs prev</span>
      </div>
    )}
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const LoadingState = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-12 h-12 border-3 border-gray-900 border-t-transparent rounded-full animate-spin mb-4" />
    <p className="text-gray-500">{text}</p>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="text-center py-12 text-gray-400 text-sm">{text}</div>
);

const SectionCard = ({ title, icon, children, action }) => (
  <div className="bg-white rounded-[9px] border border-gray-200 shadow-sm mb-6">
    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
      <h2 className="text-base font-semibold text-[#2C1810] font-fell flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {action}
    </div>
    {children}
  </div>
);

const ExportButton = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-[7px] hover:bg-gray-100 transition-colors">
    <Download className="w-3.5 h-3.5" />
    Export CSV
  </button>
);

const AlertCard = ({ alert, onDismiss }) => {
  const style = ALERT_STYLES[alert.type] || ALERT_STYLES.info;
  const Icon = style.icon;
  return (
    <div className={`${style.bg} ${style.border} border rounded-[9px] p-4 flex items-start gap-3`}>
      <Icon className={`w-5 h-5 ${style.iconColor} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${style.text}`}>{alert.title}</p>
        <p className={`text-xs ${style.text} opacity-80 mt-0.5`}>{alert.message}</p>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600 shrink-0">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const ConversionBadge = ({ rate }) => {
  const color = rate >= 20 ? 'text-emerald-700 bg-emerald-50' : rate >= 10 ? 'text-amber-700 bg-amber-50' : 'text-red-700 bg-red-50';
  return <span className={`${color} text-xs font-medium px-2 py-0.5 rounded-full`}>{rate}%</span>;
};

// ── Overview Tab ────────────────────────────────────────────────────────────
const OverviewTab = ({ overview, topProducts, statusData, funnel, funnelLoading, comparison, alerts, dismissedAlerts, onDismissAlert }) => {
  const statusTotal = statusData
    ? statusData.pending + statusData.in_progress + statusData.ready + statusData.delivered
    : 0;

  const statusSegments = statusData && statusTotal > 0 ? [
    { label: 'Pending', count: statusData.pending, color: '#F97316' },
    { label: 'In Progress', count: statusData.in_progress, color: '#F59E0B' },
    { label: 'Ready', count: statusData.ready, color: '#3B82F6' },
    { label: 'Delivered', count: statusData.delivered, color: '#10B981' },
  ] : [];

  const funnelMax = funnel ? Math.max(...funnel.map(f => f.sessions), 1) : 1;

  const visibleAlerts = (alerts || []).filter((_, i) => !dismissedAlerts.has(i));
  const g = comparison?.growth;

  // Quick summary
  const curOrders = comparison?.current?.orders || 0;
  const curRevenue = comparison?.current?.revenue || 0;
  const ordersGrowth = g?.orders;

  return (
    <>
      {/* Smart Alerts */}
      {visibleAlerts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {visibleAlerts.map((alert, idx) => {
            const realIndex = (alerts || []).indexOf(alert);
            return <AlertCard key={realIndex} alert={alert} onDismiss={() => onDismissAlert(realIndex)} />;
          })}
        </div>
      )}

      {/* Quick Summary */}
      {comparison && curOrders > 0 && (
        <div className="bg-gradient-to-r from-[#FEF3ED] to-white border border-[#d45614]/10 rounded-[9px] p-4 mb-6">
          <p className="text-sm text-[#2C1810]">
            You had <span className="font-bold">{curOrders} orders</span> worth <span className="font-bold">{fmt(curRevenue)}</span> this period
            {ordersGrowth !== undefined && ordersGrowth !== 0 && (
              <span className={ordersGrowth > 0 ? 'text-emerald-600' : 'text-red-500'}>
                {ordersGrowth > 0 ? ', up' : ', down'} {Math.abs(ordersGrowth)}% from previous period
              </span>
            )}
          </p>
        </div>
      )}

      {/* Stat Cards with Growth */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="Total Orders" value={overview?.total_orders || 0} icon={<ShoppingBag className="w-5 h-5" />} color="#d45614" bg="#FEF3ED" sub={overview?.avg_order_value > 0 ? `Avg: ${fmt(overview.avg_order_value)}` : null} growth={g?.orders} />
        <StatCard label="Total Revenue" value={fmt(overview?.total_revenue)} icon={<IndianRupee className="w-5 h-5" />} color="#10B981" bg="#D1FAE5" growth={g?.revenue} />
        <StatCard label="This Month Orders" value={overview?.this_month_orders || 0} icon={<TrendingUp className="w-5 h-5" />} color="#3B82F6" bg="#DBEAFE" growth={g?.views} />
        <StatCard label="This Month Revenue" value={fmt(overview?.this_month_revenue)} icon={<IndianRupee className="w-5 h-5" />} color="#8B5CF6" bg="#EDE9FE" growth={g?.visitors} />
      </div>

      {/* Conversion Funnel */}
      <SectionCard title="Conversion Funnel" icon={<ArrowDown className="w-4 h-4 text-[#d45614]" />}>
        <div className="p-5">
          {funnelLoading ? (
            <div className="text-center py-4 text-gray-400 text-sm">Loading funnel...</div>
          ) : funnel && funnel.length > 0 ? (
            <div className="space-y-3">
              {funnel.map((step, i) => {
                const prevSessions = i > 0 ? funnel[i - 1].sessions : step.sessions;
                const dropRate = prevSessions > 0 && i > 0
                  ? Math.round(((prevSessions - step.sessions) / prevSessions) * 100)
                  : null;
                return (
                  <div key={step.stage} className="flex items-center gap-3">
                    <span className="w-20 text-sm text-gray-600 text-right shrink-0 font-medium">
                      {FUNNEL_LABELS[step.stage] || step.stage}
                    </span>
                    <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                      <div
                        className="h-full rounded-full flex items-center px-3 text-white text-xs font-medium transition-all duration-500"
                        style={{
                          width: `${Math.max((step.sessions / funnelMax) * 100, 8)}%`,
                          backgroundColor: FUNNEL_COLORS[i] || '#9CA3AF',
                        }}
                      >
                        {step.sessions} sessions
                      </div>
                    </div>
                    {dropRate !== null && dropRate > 0 && (
                      <span className="text-xs text-red-400 w-14 text-right shrink-0">-{dropRate}%</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState text="No funnel data yet" />
          )}
        </div>
      </SectionCard>

      {/* Top Products by Orders */}
      <SectionCard title="Top Products (by orders)" icon={<Package className="w-4 h-4 text-[#d45614]" />}>
        {topProducts.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {topProducts.map((p, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-3">
                <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 shrink-0">{i + 1}</span>
                {p.img ? (
                  <img src={p.img} alt={p.name} className="w-10 h-10 rounded-[6px] object-cover shrink-0 bg-gray-100" />
                ) : (
                  <div className="w-10 h-10 rounded-[6px] bg-gray-100 flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 text-gray-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2C1810] truncate">{p.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-[#2C1810]">{p.orders} orders</p>
                  <p className="text-xs text-gray-400">{fmt(p.revenue)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState text="No order data yet" />
        )}
      </SectionCard>

      {/* Orders by Status */}
      <SectionCard title="Orders by Status" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
        <div className="p-5">
          {statusTotal > 0 ? (
            <>
              <div className="flex h-8 rounded-full overflow-hidden mb-4">
                {statusSegments.map((seg, i) => (
                  seg.count > 0 && (
                    <div key={i} style={{ width: `${(seg.count / statusTotal) * 100}%`, backgroundColor: seg.color }}
                      className="flex items-center justify-center text-white text-xs font-medium min-w-[32px]">{seg.count}</div>
                  )
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                {statusSegments.map((seg, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: seg.color }} />
                    <span className="text-sm text-gray-600">{seg.label}</span>
                    <span className="text-sm font-semibold text-[#2C1810]">{seg.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState text="No order data yet" />
          )}
        </div>
      </SectionCard>
    </>
  );
};

// ── Traffic Tab ─────────────────────────────────────────────────────────────
const TrafficTab = ({ traffic, referrers, peakHours, devices, loading }) => {
  if (loading) return <LoadingState text="Loading traffic data..." />;
  if (!traffic) return <EmptyState text="No traffic data yet. Page views will appear here once customers visit your store." />;

  const maxDaily = Math.max(...(traffic.daily_views || []).map(d => d.views), 1);

  const handleExportTraffic = () => {
    const headers = ['Date', 'Views'];
    const rows = (traffic.daily_views || []).map(d => [d.date, d.views]);
    downloadCSV(headers, rows, 'traffic_data');
  };

  return (
    <>
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="Total Page Views" value={traffic.total_views} icon={<Eye className="w-5 h-5" />} color="#d45614" bg="#FEF3ED" sub={`Last ${traffic.days} days`} />
        <StatCard label="Unique Visitors" value={traffic.unique_visitors} icon={<Users className="w-5 h-5" />} color="#10B981" bg="#D1FAE5" />
        <StatCard label="New Visitors" value={traffic.new_visitors ?? 0} icon={<UserPlus className="w-5 h-5" />} color="#3B82F6" bg="#DBEAFE" />
        <StatCard label="Returning Visitors" value={traffic.returning_visitors ?? 0} icon={<UserCheck className="w-5 h-5" />} color="#8B5CF6" bg="#EDE9FE" />
      </div>

      {/* Daily Views */}
      {traffic.daily_views && traffic.daily_views.length > 0 && (
        <SectionCard title="Daily Views" icon={<TrendingUp className="w-4 h-4 text-[#d45614]" />} action={<ExportButton onClick={handleExportTraffic} />}>
          <div className="p-5">
            <div className="flex items-end gap-1 h-40">
              {traffic.daily_views.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                  <div className="absolute -top-6 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {d.date}: {d.views} views
                  </div>
                  <div
                    className="w-full rounded-t-sm transition-all duration-200 group-hover:opacity-80"
                    style={{ height: `${Math.max((d.views / maxDaily) * 100, 2)}%`, backgroundColor: '#d45614', minHeight: '2px' }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-400">
              <span>{traffic.daily_views[0]?.date}</span>
              <span>{traffic.daily_views[traffic.daily_views.length - 1]?.date}</span>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Views by Page Type + Referrers side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {traffic.by_page_type && traffic.by_page_type.length > 0 && (
          <SectionCard title="Views by Page Type" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-5 space-y-3">
              {traffic.by_page_type.map((item, i) => {
                const maxCount = traffic.by_page_type[0]?.count || 1;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-16 text-xs text-gray-600 text-right shrink-0">
                      {PAGE_TYPE_LABELS[item.page_type] || item.page_type}
                    </span>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div
                        className="h-full rounded-full flex items-center justify-end pr-2 text-white text-xs font-medium"
                        style={{ width: `${Math.max((item.count / maxCount) * 100, 8)}%`, backgroundColor: PAGE_TYPE_COLORS[item.page_type] || '#9CA3AF' }}
                      >
                        {item.count}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {referrers && referrers.length > 0 && (
          <SectionCard title="Traffic Sources" icon={<Globe className="w-4 h-4 text-[#d45614]" />}>
            <div className="divide-y divide-gray-100">
              {referrers.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-2.5">
                  <span className="text-sm text-gray-700">{r.source}</span>
                  <span className="text-sm font-semibold text-[#2C1810]">{r.visits}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}
      </div>

      {/* Device Breakdown */}
      {devices && devices.length > 0 && (
        <SectionCard title="Device Breakdown" icon={<Monitor className="w-4 h-4 text-[#d45614]" />}>
          <div className="p-5">
            <div className="flex items-center gap-2 h-10 rounded-full overflow-hidden mb-5">
              {devices.map((d) => (
                <div
                  key={d.device}
                  className="h-full flex items-center justify-center text-white text-xs font-medium transition-all"
                  style={{ width: `${d.percent}%`, backgroundColor: DEVICE_COLORS[d.device] || '#9CA3AF', minWidth: d.percent > 0 ? '40px' : '0' }}
                >
                  {d.percent > 8 && `${d.percent}%`}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {devices.map((d) => {
                const DeviceIcon = d.device === 'Mobile' ? Smartphone : d.device === 'Tablet' ? Tablet : Monitor;
                return (
                  <div key={d.device} className="flex items-center gap-3 p-3 bg-gray-50 rounded-[9px]">
                    <DeviceIcon className="w-5 h-5" style={{ color: DEVICE_COLORS[d.device] }} />
                    <div>
                      <p className="text-sm font-semibold text-[#2C1810]">{d.device}</p>
                      <p className="text-xs text-gray-500">{d.views} views ({d.percent}%)</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Peak Hours + Day of Week side by side */}
      {peakHours && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard title="Views by Hour" icon={<Clock className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-5">
              <div className="flex items-end gap-[2px] h-28">
                {peakHours.hours.map((h) => {
                  const maxH = Math.max(...peakHours.hours.map(x => x.views), 1);
                  return (
                    <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                      <div className="absolute -top-6 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                        {h.hour}:00 — {h.views} views
                      </div>
                      <div
                        className="w-full rounded-t-sm group-hover:opacity-80"
                        style={{ height: `${Math.max((h.views / maxH) * 100, 2)}%`, backgroundColor: h.views === Math.max(...peakHours.hours.map(x => x.views)) ? '#d45614' : '#e5e7eb', minHeight: '2px' }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-gray-400">
                <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Views by Day" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-5 space-y-2">
              {peakHours.weekdays.map((d) => {
                const maxD = Math.max(...peakHours.weekdays.map(x => x.views), 1);
                return (
                  <div key={d.day} className="flex items-center gap-3">
                    <span className="w-8 text-xs text-gray-600 text-right shrink-0">{d.day}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                      <div
                        className="h-full rounded-full flex items-center justify-end pr-2 text-white text-[10px] font-medium"
                        style={{ width: `${Math.max((d.views / maxD) * 100, 5)}%`, backgroundColor: '#d45614' }}
                      >
                        {d.views}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </div>
      )}
    </>
  );
};

// ── Products Tab ────────────────────────────────────────────────────────────
const ProductsTab = ({ topPages, categories, conversion, abandonedCarts, loading }) => {
  if (loading) return <LoadingState text="Loading product data..." />;

  const handleExportProducts = () => {
    if (!conversion || !conversion.length) return;
    const headers = ['Product', 'Views', 'Cart Adds', 'Orders', 'View→Cart %', 'Cart→Order %'];
    const rows = conversion.map(p => [p.name, p.views, p.cart_adds, p.orders, p.view_to_cart, p.cart_to_order]);
    downloadCSV(headers, rows, 'product_conversion');
  };

  return (
    <>
      {/* Product Conversion Funnel */}
      <SectionCard title="Product Conversion Funnel" icon={<ShoppingCart className="w-4 h-4 text-[#d45614]" />} action={conversion && conversion.length > 0 && <ExportButton onClick={handleExportProducts} />}>
        {conversion && conversion.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-5 py-2 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase">
              <div className="col-span-4">Product</div>
              <div className="col-span-1 text-right">Views</div>
              <div className="col-span-2 text-right">Cart Adds</div>
              <div className="col-span-1 text-right">Orders</div>
              <div className="col-span-2 text-right">View→Cart</div>
              <div className="col-span-2 text-right">Cart→Order</div>
            </div>
            <div className="divide-y divide-gray-100">
              {conversion.map((p) => (
                <div key={p.product_id} className="grid grid-cols-12 items-center px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="col-span-4 flex items-center gap-3">
                    {p.img ? (
                      <img src={p.img} alt={p.name} className="w-8 h-8 rounded-[6px] object-cover shrink-0 bg-gray-100" />
                    ) : (
                      <div className="w-8 h-8 rounded-[6px] bg-gray-100 flex items-center justify-center shrink-0">
                        <Package className="w-4 h-4 text-gray-300" />
                      </div>
                    )}
                    <p className="text-sm font-medium text-[#2C1810] truncate">{p.name}</p>
                  </div>
                  <div className="col-span-1 text-right"><p className="text-sm text-gray-700">{p.views}</p></div>
                  <div className="col-span-2 text-right"><p className="text-sm text-gray-700">{p.cart_adds}</p></div>
                  <div className="col-span-1 text-right"><p className="text-sm font-semibold text-[#2C1810]">{p.orders}</p></div>
                  <div className="col-span-2 text-right"><ConversionBadge rate={p.view_to_cart} /></div>
                  <div className="col-span-2 text-right"><ConversionBadge rate={p.cart_to_order} /></div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No product conversion data yet. Views and cart additions will appear here." />
        )}
      </SectionCard>

      {/* Abandoned Carts */}
      <SectionCard title="Abandoned Carts" icon={<ShoppingCart className="w-4 h-4 text-amber-500" />}>
        {abandonedCarts && abandonedCarts.total_abandoned_carts > 0 ? (
          <>
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
              <p className="text-sm text-amber-800">
                <span className="font-semibold">{abandonedCarts.total_abandoned_carts}</span> abandoned carts worth{' '}
                <span className="font-semibold">{fmt(abandonedCarts.total_abandoned_value)}</span> — recovery opportunity
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {(abandonedCarts.carts || []).slice(0, 10).map((c) => (
                <div key={c.customer_id} className="px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <span className="text-sm font-medium text-[#2C1810]">{c.customer_name}</span>
                      <span className="text-xs text-gray-400 ml-2">{c.email}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#2C1810]">{fmt(c.total_value)}</p>
                      <p className="text-xs text-gray-400">{c.item_count} item{c.item_count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No abandoned carts found." />
        )}
      </SectionCard>

      {/* Most Viewed Products */}
      <SectionCard title="Most Viewed Products" icon={<Eye className="w-4 h-4 text-[#d45614]" />}>
        {topPages && topPages.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-5 py-2 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase">
              <div className="col-span-1">#</div>
              <div className="col-span-6">Product</div>
              <div className="col-span-3 text-right">Views</div>
              <div className="col-span-2 text-right">Visitors</div>
            </div>
            <div className="divide-y divide-gray-100">
              {topPages.map((p, i) => (
                <div key={p.product_id} className="grid grid-cols-12 items-center px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="col-span-1">
                    <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">{i + 1}</span>
                  </div>
                  <div className="col-span-6 flex items-center gap-3">
                    {p.img ? (
                      <img src={p.img} alt={p.name} className="w-10 h-10 rounded-[6px] object-cover shrink-0 bg-gray-100" />
                    ) : (
                      <div className="w-10 h-10 rounded-[6px] bg-gray-100 flex items-center justify-center shrink-0">
                        <Package className="w-5 h-5 text-gray-300" />
                      </div>
                    )}
                    <p className="text-sm font-medium text-[#2C1810] truncate">{p.name}</p>
                  </div>
                  <div className="col-span-3 text-right">
                    <p className="text-sm font-semibold text-[#2C1810]">{p.views}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-500">{p.unique_visitors}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No product view data yet." />
        )}
      </SectionCard>

      {/* Category Performance */}
      <SectionCard title="Category Performance" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
        {categories && categories.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-5 py-2 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase">
              <div className="col-span-4">Category</div>
              <div className="col-span-2 text-right">Views</div>
              <div className="col-span-2 text-right">Visitors</div>
              <div className="col-span-2 text-right">Orders</div>
              <div className="col-span-2 text-right">Revenue</div>
            </div>
            <div className="divide-y divide-gray-100">
              {categories.map((c, i) => (
                <div key={i} className="grid grid-cols-12 items-center px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="col-span-4">
                    <p className="text-sm font-medium text-[#2C1810] truncate">{c.category}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-700">{c.views}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-500">{c.visitors}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm font-semibold text-[#2C1810]">{c.orders}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-500">{fmt(c.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No category data yet." />
        )}
      </SectionCard>
    </>
  );
};

// ── Customers Tab ───────────────────────────────────────────────────────────
const CustomersTab = ({ authAxios, customerValue }) => {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCustomers = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    try {
      const res = await authAxios.get('/analytics/customers/', { params: { page: p, search: q, page_size: 15 } });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setPage(res.data.page || 1);
    } catch (err) {
      devLog.error('Customer fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchCustomers(1, ''); }, [fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(1, search);
  };

  const summary = customerValue?.summary;
  const topCustomers = customerValue?.top_customers || [];

  const handleExportCustomers = () => {
    if (!topCustomers.length) return;
    const headers = ['Name', 'Email', 'Total Orders', 'Total Spend', 'Avg Order Value', 'First Order', 'Last Order'];
    const rows = topCustomers.map(c => [c.name, c.email, c.total_orders, c.total_spend, c.avg_order_value, c.first_order, c.last_order]);
    downloadCSV(headers, rows, 'customer_value');
  };

  return (
    <>
      {/* LTV Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <StatCard label="Total Customers" value={summary.total_customers} icon={<Users className="w-5 h-5" />} color="#d45614" bg="#FEF3ED" />
          <StatCard label="Repeat Buyers" value={summary.repeat_buyers} icon={<Repeat className="w-5 h-5" />} color="#10B981" bg="#D1FAE5" />
          <StatCard label="Repeat Rate" value={`${summary.repeat_rate}%`} icon={<TrendingUp className="w-5 h-5" />} color="#3B82F6" bg="#DBEAFE" />
          <StatCard label="Avg Lifetime Value" value={fmt(summary.avg_ltv)} icon={<Star className="w-5 h-5" />} color="#8B5CF6" bg="#EDE9FE" />
        </div>
      )}

      {/* Top Customers by Spend */}
      {topCustomers.length > 0 && (
        <SectionCard title="Top Customers by Spend" icon={<Star className="w-4 h-4 text-[#d45614]" />} action={<ExportButton onClick={handleExportCustomers} />}>
          <div className="hidden sm:grid grid-cols-12 px-5 py-2 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-1 text-right">Orders</div>
            <div className="col-span-2 text-right">Total Spend</div>
            <div className="col-span-1 text-right">Avg</div>
            <div className="col-span-2 text-right">Last Order</div>
          </div>
          <div className="divide-y divide-gray-100">
            {topCustomers.map((c) => (
              <div key={c.customer_id} className="grid grid-cols-12 items-center px-5 py-3 hover:bg-gray-50 transition-colors">
                <div className="col-span-3"><p className="text-sm font-medium text-[#2C1810] truncate">{c.name}</p></div>
                <div className="col-span-3"><p className="text-sm text-gray-600 truncate">{c.email}</p></div>
                <div className="col-span-1 text-right"><p className="text-sm text-gray-700">{c.total_orders}</p></div>
                <div className="col-span-2 text-right"><p className="text-sm font-semibold text-[#2C1810]">{fmt(c.total_spend)}</p></div>
                <div className="col-span-1 text-right"><p className="text-xs text-gray-500">{fmt(c.avg_order_value)}</p></div>
                <div className="col-span-2 text-right">
                  <p className="text-sm text-gray-500">
                    {c.last_order ? new Date(c.last_order).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Customer List */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500">{total} registered customer{total !== 1 ? 's' : ''}</p>
        <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search name, email, phone..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-64 pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-[9px] focus:outline-none focus:ring-2 focus:ring-[#d45614]/20 focus:border-[#d45614]" />
          </div>
          <button type="submit" className="px-4 py-2 text-sm bg-[#d45614] text-white rounded-[9px] hover:bg-[#b8480f] transition-colors">Search</button>
        </form>
      </div>

      <div className="bg-white rounded-[9px] border border-gray-200 shadow-sm overflow-hidden">
        {loading ? <LoadingState text="Loading customers..." /> : customers.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-5 py-2 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase">
              <div className="col-span-3">Name</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-2">Phone</div>
              <div className="col-span-2">Joined</div>
              <div className="col-span-1 text-center">Verified</div>
            </div>
            <div className="divide-y divide-gray-100">
              {customers.map((c) => (
                <div key={c.id} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-0 items-center px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="sm:col-span-3"><p className="text-sm font-medium text-[#2C1810]">{c.username}</p></div>
                  <div className="sm:col-span-4"><p className="text-sm text-gray-600 truncate">{c.email}</p></div>
                  <div className="sm:col-span-2"><p className="text-sm text-gray-600">{c.phone || '—'}</p></div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500">
                      {c.date_joined ? new Date(c.date_joined).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </p>
                  </div>
                  <div className="sm:col-span-1 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${c.is_verified ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
                <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
                <div className="flex gap-1">
                  <button onClick={() => fetchCustomers(page - 1, search)} disabled={page <= 1}
                    className="p-1.5 rounded-[9px] border border-gray-200 disabled:opacity-40 hover:bg-white transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => fetchCustomers(page + 1, search)} disabled={page >= totalPages}
                    className="p-1.5 rounded-[9px] border border-gray-200 disabled:opacity-40 hover:bg-white transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyState text={search ? `No customers matching "${search}"` : 'No registered customers yet.'} />
        )}
      </div>
    </>
  );
};

// ── Insights Tab ────────────────────────────────────────────────────────────
const InsightsTab = ({ alerts, comparison, conversion, abandonedCarts, customerValue, days }) => {
  const g = comparison?.growth;
  const cur = comparison?.current;
  const prev = comparison?.previous;

  // Best selling product from conversion data
  const bestProduct = conversion && conversion.length > 0
    ? conversion.reduce((a, b) => (a.orders > b.orders ? a : b))
    : null;

  // Busiest day from current period
  const busiestMetric = cur?.views > 0 ? `${cur.views} views in last ${days} days` : null;

  return (
    <>
      {/* Smart Alerts (full width) */}
      <SectionCard title="Smart Alerts" icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}>
        {alerts && alerts.length > 0 ? (
          <div className="p-5 space-y-3">
            {alerts.map((alert, i) => <AlertCard key={i} alert={alert} />)}
          </div>
        ) : (
          <EmptyState text="No alerts right now — everything looks good!" />
        )}
      </SectionCard>

      {/* Period Comparison */}
      {comparison && (
        <SectionCard title={`Period Comparison (${days} days)`} icon={<TrendingUp className="w-4 h-4 text-[#d45614]" />}>
          <div className="p-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Orders', cur: cur?.orders, prev: prev?.orders, growth: g?.orders },
                { label: 'Revenue', cur: fmt(cur?.revenue), prev: fmt(prev?.revenue), growth: g?.revenue, raw: true },
                { label: 'Page Views', cur: cur?.views, prev: prev?.views, growth: g?.views },
                { label: 'Visitors', cur: cur?.visitors, prev: prev?.visitors, growth: g?.visitors },
              ].map((m) => (
                <div key={m.label} className="bg-gray-50 rounded-[9px] p-4">
                  <p className="text-xs text-gray-500 mb-1">{m.label}</p>
                  <div className="flex items-baseline gap-3">
                    <div>
                      <p className="text-lg font-bold text-[#2C1810]">{m.cur}</p>
                      <p className="text-xs text-gray-400">Current</p>
                    </div>
                    <div className="text-gray-300">vs</div>
                    <div>
                      <p className="text-lg font-semibold text-gray-400">{m.prev}</p>
                      <p className="text-xs text-gray-400">Previous</p>
                    </div>
                  </div>
                  {m.growth !== undefined && (
                    <div className={`flex items-center gap-1 mt-2 ${m.growth >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {m.growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      <span className="text-sm font-medium">{m.growth >= 0 ? '+' : ''}{m.growth}%</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Key Metrics Summary */}
      <SectionCard title="Key Metrics" icon={<Star className="w-4 h-4 text-[#d45614]" />}>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bestProduct && (
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-white border border-amber-100 rounded-[9px]">
                <Package className="w-8 h-8 text-amber-500 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Best Selling Product</p>
                  <p className="text-sm font-semibold text-[#2C1810] truncate">{bestProduct.name}</p>
                  <p className="text-xs text-gray-400">{bestProduct.orders} orders</p>
                </div>
              </div>
            )}
            {busiestMetric && (
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-white border border-blue-100 rounded-[9px]">
                <Eye className="w-8 h-8 text-blue-500 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Total Traffic</p>
                  <p className="text-sm font-semibold text-[#2C1810]">{busiestMetric}</p>
                </div>
              </div>
            )}
            {customerValue?.summary?.repeat_rate > 0 && (
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-50 to-white border border-emerald-100 rounded-[9px]">
                <Repeat className="w-8 h-8 text-emerald-500 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Customer Retention</p>
                  <p className="text-sm font-semibold text-[#2C1810]">{customerValue.summary.repeat_rate}% repeat rate</p>
                  <p className="text-xs text-gray-400">{customerValue.summary.repeat_buyers} repeat buyers</p>
                </div>
              </div>
            )}
            {abandonedCarts?.total_abandoned_carts > 0 && (
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-red-50 to-white border border-red-100 rounded-[9px]">
                <ShoppingCart className="w-8 h-8 text-red-500 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Abandoned Carts</p>
                  <p className="text-sm font-semibold text-[#2C1810]">{abandonedCarts.total_abandoned_carts} carts</p>
                  <p className="text-xs text-gray-400">{fmt(abandonedCarts.total_abandoned_value)} potential revenue</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Quick Action Suggestions */}
      {alerts && alerts.length > 0 && (
        <SectionCard title="Suggested Actions" icon={<Lightbulb className="w-4 h-4 text-amber-500" />}>
          <div className="p-5 space-y-2">
            {alerts.map((alert, i) => {
              let action = '';
              if (alert.icon === 'alert' && alert.title.includes('Stock')) action = 'Consider restocking this product to capture demand';
              else if (alert.icon === 'cart') action = 'Send follow-up emails to customers with abandoned carts';
              else if (alert.title.includes('No Recent')) action = 'Consider running a promotion or sharing on social media';
              else if (alert.icon === 'trending_up') action = 'Great momentum! Keep up your marketing efforts';
              else if (alert.icon === 'trending_down') action = 'Review your marketing channels and try new promotions';
              else if (alert.icon === 'globe') action = `Double down on ${alert.message.split('from ')[1] || 'this channel'}`;
              if (!action) return null;
              return (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-[9px]">
                  <Lightbulb className="w-4 h-4 text-amber-400 shrink-0" />
                  <p className="text-sm text-gray-700">{action}</p>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </>
  );
};

// ── Main Analytics Component ────────────────────────────────────────────────
const Analytics = () => {
  const { authToken } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [days, setDays] = useState(30);

  // Overview
  const [overview, setOverview] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Funnel
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);

  // Comparison & Alerts
  const [comparison, setComparison] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  // Traffic
  const [traffic, setTraffic] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [devices, setDevices] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);

  // Products
  const [topPages, setTopPages] = useState(null);
  const [categories, setCategories] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [abandonedCarts, setAbandonedCarts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);

  // Customers
  const [customerValue, setCustomerValue] = useState(null);

  const authAxios = useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: { Authorization: `Bearer ${authToken?.access}` },
  }), [authToken?.access]);

  // Fetch overview on mount
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const [overviewRes, topRes, statusRes] = await Promise.all([
          authAxios.get('/analytics/overview/'),
          authAxios.get('/analytics/top_products/'),
          authAxios.get('/analytics/orders_by_status/'),
        ]);
        setOverview(overviewRes.data);
        setTopProducts(topRes.data.products || []);
        setStatusData(statusRes.data);
      } catch (err) {
        devLog.error('Analytics fetch error:', err);
      } finally {
        setOverviewLoading(false);
      }
    };
    fetchOverview();
  }, [authAxios]);

  // Fetch comparison + alerts when overview or insights tab active, or days change
  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'insights') {
      Promise.all([
        authAxios.get('/analytics/comparison/', { params: { days } }),
        authAxios.get('/analytics/alerts/'),
      ]).then(([compRes, alertRes]) => {
        setComparison(compRes.data);
        setAlerts(alertRes.data.alerts || []);
      }).catch(err => devLog.error('Comparison/alerts fetch error:', err));
    }
  }, [activeTab, days, authAxios]);

  // Fetch funnel when overview tab active or days change
  useEffect(() => {
    if (activeTab === 'overview') {
      setFunnelLoading(true);
      authAxios.get('/analytics/funnel/', { params: { days } })
        .then(res => setFunnel(res.data.funnel || []))
        .catch(err => devLog.error('Funnel fetch error:', err))
        .finally(() => setFunnelLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch traffic data when traffic tab active or days change
  useEffect(() => {
    if (activeTab === 'traffic') {
      setTrafficLoading(true);
      Promise.all([
        authAxios.get('/analytics/traffic/', { params: { days } }),
        authAxios.get('/analytics/referrers/', { params: { days } }),
        authAxios.get('/analytics/peak_hours/', { params: { days } }),
        authAxios.get('/analytics/devices/', { params: { days } }),
      ]).then(([trafficRes, refRes, peakRes, devRes]) => {
        setTraffic(trafficRes.data);
        setReferrers(refRes.data.referrers || []);
        setPeakHours(peakRes.data);
        setDevices(devRes.data.devices || []);
      }).catch(err => devLog.error('Traffic fetch error:', err))
        .finally(() => setTrafficLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch products data when products or insights tab active or days change
  useEffect(() => {
    if (activeTab === 'products' || activeTab === 'insights') {
      setProductsLoading(true);
      Promise.all([
        authAxios.get('/analytics/top_pages/', { params: { days } }),
        authAxios.get('/analytics/categories/', { params: { days } }),
        authAxios.get('/analytics/product_conversion/', { params: { days } }),
        authAxios.get('/analytics/abandoned_carts/'),
      ]).then(([topRes, catRes, convRes, cartRes]) => {
        setTopPages(topRes.data.products || []);
        setCategories(catRes.data.categories || []);
        setConversion(convRes.data.products || []);
        setAbandonedCarts(cartRes.data);
      }).catch(err => devLog.error('Products fetch error:', err))
        .finally(() => setProductsLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch customer value when customers or insights tab active
  useEffect(() => {
    if (activeTab === 'customers' || activeTab === 'insights') {
      authAxios.get('/analytics/customer_value/')
        .then(res => setCustomerValue(res.data))
        .catch(err => devLog.error('Customer value fetch error:', err));
    }
  }, [activeTab, authAxios]);

  const handleDismissAlert = useCallback((index) => {
    setDismissedAlerts(prev => new Set([...prev, index]));
  }, []);

  return (
    <Base
      Content={
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 overflow-x-hidden">
          {/* Header + Day Selector */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[9px] bg-[#d45614] flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#2C1810] font-fell">Analytics</h1>
            </div>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-[9px]">
              {DAY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDays(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-[7px] transition-all ${
                    days === opt.value ? 'bg-white text-[#d45614] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-[9px] overflow-x-auto scrollbar-hide">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-[7px] transition-all whitespace-nowrap ${
                    activeTab === tab.id ? 'bg-white text-[#d45614] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            overviewLoading ? <LoadingState text="Loading analytics..." />
              : <OverviewTab overview={overview} topProducts={topProducts} statusData={statusData} funnel={funnel} funnelLoading={funnelLoading} comparison={comparison} alerts={alerts} dismissedAlerts={dismissedAlerts} onDismissAlert={handleDismissAlert} />
          )}
          {activeTab === 'traffic' && <TrafficTab traffic={traffic} referrers={referrers} peakHours={peakHours} devices={devices} loading={trafficLoading} />}
          {activeTab === 'products' && <ProductsTab topPages={topPages} categories={categories} conversion={conversion} abandonedCarts={abandonedCarts} loading={productsLoading} />}
          {activeTab === 'customers' && <CustomersTab authAxios={authAxios} customerValue={customerValue} />}
          {activeTab === 'insights' && <InsightsTab alerts={alerts} comparison={comparison} conversion={conversion} abandonedCarts={abandonedCarts} customerValue={customerValue} days={days} />}
        </div>
      }
    />
  );
};

export default Analytics;
